'use client';

import NameCell from '@/components/publicSearch/ResultCell/NameCell';
import { GET_ANIMALS, setDefaultHeader } from '@/studbook/graphql/queries';
import {
  Entity,
  GetAnimalDetailsResponse,
  GetAnimalsResponse,
  SortDirection,
} from '@/studbook/graphql/results';
import { useLazyQuery } from '@apollo/client';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import useAnimalDetailFilter from './useAnimalDetailFilter';
import { useEffect, useMemo, useState } from 'react';
import useAnimalFilterStore from '@/studbook/store/useAnimalFilterStore';
import { Box, IconButton, styled, Tooltip } from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import Image from 'next/image';
import { HyperLink } from '@/components/publicSearch/ResultCell/HyperLink';
import {
  LogicalOperatorText,
  ComparisonOperatorText,
  ComparisonOperator,
} from '@/studbook/constants';
import { PUBLIC_ROUTES } from '@/configs/routes';
import useMemoWithPrevious from '../common/useMemoWithPrevious';
import MemberInlineDisplay from '@/components/publicSearch/Member/MemberInlineDisplay';
import { parseJson, removeDuplicates } from '@/lib/common';
import useGridStore from '@/studbook/store/useGridStore';
import useOrgNameFromOrgId from '../useOrgNameFromOrgId';
import { ENTITY_DETAIL_NAME } from '@/model/form-builder';
import { SexType } from '@/apiClient';

export type BreedPercentItem = {
  breed: {
    animalSpeciesBreedEntityId: string;
    animalSpeciesBreedId: number;
    animalSpeciesId: number;
    description: string;
    name: string;
  };
  id: number;
  percentage: number;
};

const StatusBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>(({ isActive, theme }) => ({
  height: '32px',
  width: 'fit-content',
  borderRadius: '8px',
  padding: '8px',
  color: isActive ? '#047857' : 'rgba(0, 0, 0, 0.50)',
  fontSize: '12px',
  letterSpacing: '0.10px',
  lineHeight: '16px',
  textTransform: 'capitalize',
  backgroundColor: isActive ? '#D1FAE5' : '#00000033',
}));

export const DEFAULT_ANIMAL_DETAIL = {
  ID: 'id',
  GUID: 'guid',
  ANIMAL_IDENT: ENTITY_DETAIL_NAME.ANIMAL_IDENT,
  ANIMAL_NAME: ENTITY_DETAIL_NAME.ANIMAL_NAME,
  ANIMAL_STATUS: ENTITY_DETAIL_NAME.ANIMAL_STATUS,
  SIRE: ENTITY_DETAIL_NAME.SIRE,
  SEX_CODE: ENTITY_DETAIL_NAME.SEX_CODE,
  TATTOO: ENTITY_DETAIL_NAME.TATTOO,
  DAM: ENTITY_DETAIL_NAME.DAM,
  DATE_OF_BIRTH: ENTITY_DETAIL_NAME.DATE_OF_BIRTH,
  BREEDER: ENTITY_DETAIL_NAME.BREEDER,
  OWNER: ENTITY_DETAIL_NAME.OWNER,
  COLOR: ENTITY_DETAIL_NAME.COLOUR,
  BREED_PERCENTS: ENTITY_DETAIL_NAME.BREED_PERCENTS,
  BY_AI: ENTITY_DETAIL_NAME.BY_AI,
  BY_ET: ENTITY_DETAIL_NAME.BY_ET,
  DISPOSAL_RECORD: ENTITY_DETAIL_NAME.DISPOSAL_RECORD,
  BIRTH_WEIGHT: ENTITY_DETAIL_NAME.BIRTH_WEIGHT,
  ALTERNATE_IDENTS: ENTITY_DETAIL_NAME.ALTERNATE_IDENTS,
};

export const ANIMAL_DEFAULT_COLUMN_ORDER = [
  ENTITY_DETAIL_NAME.ANIMAL_NAME,
  ENTITY_DETAIL_NAME.ANIMAL_STATUS,
  ENTITY_DETAIL_NAME.ANIMAL_IDENT,
  ENTITY_DETAIL_NAME.DATE_OF_BIRTH,
  ENTITY_DETAIL_NAME.SEX_CODE,
  ENTITY_DETAIL_NAME.BREEDER,
  ENTITY_DETAIL_NAME.SIRE,
  ENTITY_DETAIL_NAME.DAM,
];

const BOOLEAN_DETAIL = {
  TRUE: 'true',
  FALSE: 'false',
};

const isBooleanDetailValue = (value: string) =>
  [BOOLEAN_DETAIL.TRUE, BOOLEAN_DETAIL.FALSE].includes(value.toLowerCase());

export type TRowValue = { value: string | number; id: string | number };
export type TRow = {
  [detailName: string]: TRowValue;
};

export const entityToRowData = (columnHeaders: string[]) => (r: Entity) => {
  try {
    let animalDetailName = r.entityDetailValues.find(
      (v) => v.entityDetailName === DEFAULT_ANIMAL_DETAIL.ANIMAL_NAME,
    );
    let { detailIdMap, detailValueMap } = extractDetailValuesMap(r);

    let genericAnimalDetail: TRow = {
      [DEFAULT_ANIMAL_DETAIL.ID]: {
        id: r.entityId.toString(),
        value: r.entityId,
      },
      [DEFAULT_ANIMAL_DETAIL.GUID]: {
        id: r.entityId.toString(),
        value: r.entityGuid,
      },
      [DEFAULT_ANIMAL_DETAIL.ANIMAL_NAME]: {
        id: r.entityId.toString(),
        value: animalDetailName?.value ?? '',
      },
    };
    columnHeaders.forEach((header) => {
      if (header === DEFAULT_ANIMAL_DETAIL.ANIMAL_NAME) return;

      genericAnimalDetail[header] = {
        id: detailIdMap.get(header) ?? '',
        value: detailValueMap.get(header) ?? '',
      };
    });

    return genericAnimalDetail;
  } catch (err) {
    console.error(err);
  }

  return {};
};

const entityDetailWithLink: string[] = [
  ENTITY_DETAIL_NAME.ANIMAL_NAME,
  ENTITY_DETAIL_NAME.BREEDER,
  ENTITY_DETAIL_NAME.DAM,
  ENTITY_DETAIL_NAME.NAME,
  ENTITY_DETAIL_NAME.ORIGINAL_OWNER,
  ENTITY_DETAIL_NAME.OWNER,
  ENTITY_DETAIL_NAME.SIRE,
];

export const entityToDataGridColumn = (
  entity: GetAnimalDetailsResponse['animalDetails'][0],
  orgName: string,
) => ({
  entityDetailName: entity.entityDetailName || '',
  field: entity.entityDetailId?.toString() || '',
  headerClassName: entityDetailWithLink.includes(entity.entityDetailName || '')
    ? 'pl'
    : '',
  headerName: entity.entityDetailConfigs?.labelText || '',
  sortable: true,
  width:
    entity.entityDetailName === DEFAULT_ANIMAL_DETAIL.ANIMAL_NAME
      ? 250
      : entity.entityDetailName === DEFAULT_ANIMAL_DETAIL.SEX_CODE
        ? 80
        : 150,
  minWidth: 80,
  renderCell(params: GridRenderCellParams<TRow>) {
    let value = params.row[entity.entityDetailName!].value.toString();
    const id = params.row[entity.entityDetailName!].id;
    if (isBooleanDetailValue(value.toString())) {
      return BOOLEAN_DETAIL.TRUE === value.toString().toLowerCase() ? (
        <IconButton color="success">
          <VerifiedIcon />
        </IconButton>
      ) : (
        <></>
      );
    }

    switch (entity.entityDetailName) {
      case DEFAULT_ANIMAL_DETAIL.SEX_CODE: {
        const parsedValue = parseJson(value, {});
        const parsedType = Number(parsedValue.sexType);

        if (parsedType === SexType.Male) {
          return (
            <Tooltip title="Male">
              <MaleIcon sx={{ color: 'blue', my: '14px' }} />
            </Tooltip>
          );
        }

        if (parsedType === SexType.Female) {
          return (
            <Tooltip title="Female">
              <FemaleIcon sx={{ color: 'hotpink', my: '14px' }} />
            </Tooltip>
          );
        }

        if (parsedType === SexType.DesexedMale) {
          return (
            <Tooltip title="Steer - castrated male">
              <Image
                src="/neuter-sign.png"
                alt="Steer icon"
                width={24}
                height={24}
                style={{ margin: '14px 0' }}
              />
            </Tooltip>
          );
        }

        return (
          <Tooltip title="Unknown">
            <IconButton
              sx={{
                color: 'grey',
              }}
            >
              <QuestionMarkIcon />
            </IconButton>
          </Tooltip>
        );
      }
      case DEFAULT_ANIMAL_DETAIL.ANIMAL_NAME: {
        return (
          <NameCell
            name={params.row[
              DEFAULT_ANIMAL_DETAIL.ANIMAL_NAME
            ].value.toString()}
            path={`/${orgName}${PUBLIC_ROUTES.ANIMAL}/${
              params.row[DEFAULT_ANIMAL_DETAIL.ANIMAL_NAME].id
            }`}
          />
        );
      }
      case DEFAULT_ANIMAL_DETAIL.ANIMAL_STATUS:
        return (
          <StatusBadge
            isActive={
              params.row[entity.entityDetailName].value
                .toString()
                .toLowerCase() === 'active'
            }
          >
            {String(
              params.row[entity.entityDetailName].value
                .toString()
                .toLowerCase(),
            ).toLowerCase()}
          </StatusBadge>
        );
      case DEFAULT_ANIMAL_DETAIL.BREEDER:
      case DEFAULT_ANIMAL_DETAIL.OWNER: {
        if (!id) return '';
        const name = params.row[entity.entityDetailName].value.toString();
        return (
          <MemberInlineDisplay
            memberDisplayName={name}
            memberId={Number(id)}
            orgName={orgName}
          />
        );
      }

      case DEFAULT_ANIMAL_DETAIL.SIRE:
      case DEFAULT_ANIMAL_DETAIL.DAM: {
        const pathname = `/${orgName}${PUBLIC_ROUTES.ANIMAL}/${id}`;
        if (!id) return null;
        return <HyperLink label={value} pathname={pathname} />;
      }
      case DEFAULT_ANIMAL_DETAIL.BREED_PERCENTS: {
        const breedPercents = parseJson(value, []);

        return breedPercents
          .filter((it: BreedPercentItem) => !!it.percentage)
          .map(
            (it: BreedPercentItem) => `${it.breed?.name} (${it.percentage}%)`,
          )
          .join('; ');
      }
    }

    return params.row[entity.entityDetailName!].value;
  },
});

function extractDetailValuesMap(r: Entity) {
  let detailValueMap = new Map<string, string | number>();
  let detailIdMap = new Map<string, string | number>();
  r.entityDetailValues.forEach((v) => {
    let shouldUseAddInfo = [
      DEFAULT_ANIMAL_DETAIL.SIRE.toLowerCase(),
      DEFAULT_ANIMAL_DETAIL.DAM.toLowerCase(),
      DEFAULT_ANIMAL_DETAIL.BREEDER.toLowerCase(),
      DEFAULT_ANIMAL_DETAIL.OWNER.toLowerCase(),
    ].includes(v.entityDetailName.toLowerCase());
    detailValueMap.set(
      v.entityDetailName,
      shouldUseAddInfo && v.additionalInfo ? v.additionalInfo.name : v.value,
    );
    detailIdMap.set(
      v.entityDetailName,
      shouldUseAddInfo && v.additionalInfo?.id
        ? v.additionalInfo.id
        : v.entityId,
    );
  });
  return { detailIdMap, detailValueMap };
}

export default function useAnimalFilter(
  orgId: string,
  paginationModel: { page: number; pageSize: number },
  enabled: boolean,
) {
  const { gridSortModel: sortModel } = useGridStore();
  const { filters } = useAnimalFilterStore();
  const { animalDetails } = useAnimalDetailFilter(orgId);
  const [manualOrder, setManualOrder] = useState(false);
  const [getAnimals, { data, error, loading, previousData }] =
    useLazyQuery<GetAnimalsResponse>(GET_ANIMALS);
  const orgName = useOrgNameFromOrgId(orgId);
  const startSearch = () => {
    if (!animalDetails?.animalDetails || !enabled) return;
    if (
      filters.some(
        (f) =>
          !f.detailName ||
          f.detailName.length === 0 ||
          (f.operator !== ComparisonOperator.NotNull &&
            (!f.value || f.value.length === 0)),
      )
    )
      return;

    getAnimals({
      variables: {
        orgId: Number(orgId),
        currentPage: (paginationModel.page ?? 0) + 1,
        pageSize: paginationModel.pageSize,
        filterInput: {
          filters: filters.map((f) => ({
            entityDetailName: f.detailName,
            value: f.value,
            logicalOperator:
              LogicalOperatorText[f.logicalOperator].toUpperCase(),
            operator:
              ComparisonOperatorText[
                Number(f.operator) as keyof typeof ComparisonOperatorText
              ].toUpperCase(),
          })),
        },
        sortInput: sortModel[0]
          ? {
              entityDetailId: Number(sortModel[0].field),
              sortDirection:
                sortModel[0].sort === 'asc'
                  ? SortDirection.Ascending
                  : SortDirection.Descending,
            }
          : null,
      },
      context: {
        headers: {
          ...setDefaultHeader(orgId),
        },
      },
    });
  };

  useEffect(() => {
    if (!animalDetails?.animalDetails || !enabled) return;
    startSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    animalDetails?.animalDetails,
    enabled,
    filters,
    sortModel,
    paginationModel,
  ]);

  let animalDetailsSet = removeDuplicates(
    (animalDetails && animalDetails.animalDetails) ?? [],
    (i) => i.entityDetailId?.toString() || '',
  );
  let columnHeaders = animalDetailsSet.map((d) => d.entityDetailName || '');

  const defaultAnimalDetailIdColumnOrder = ANIMAL_DEFAULT_COLUMN_ORDER.map(
    (detailName) =>
      animalDetailsSet
        .find((d) => d.entityDetailName === detailName)
        ?.entityDetailId?.toString() ?? '',
  );
  const orderAnimalColumns = (columns: GridColDef<TRow[][number]>[]) => {
    let resetOrderedColumns: GridColDef<TRow[][number]>[] = [];
    for (let columnHeader of defaultAnimalDetailIdColumnOrder) {
      let column = columns.filter(
        (c) => c.field?.toLowerCase() === columnHeader.toLowerCase(),
      )[0];
      if (column) {
        resetOrderedColumns.push(column);
        columns = columns.filter(
          (c) => c.field?.toLowerCase() !== columnHeader.toLowerCase(),
        );
      }
    }

    resetOrderedColumns = [...resetOrderedColumns, ...columns];
    setManualOrder(true);
    return resetOrderedColumns;
  };

  const columns: GridColDef<TRow[][number]>[] = useMemoWithPrevious(
    () =>
      orderAnimalColumns([
        ...animalDetailsSet.map((d) =>
          entityToDataGridColumn(d, orgName ?? ''),
        ),
      ]),
    [animalDetails, manualOrder, orgName],
    (previousColumns, calculatedColumns) =>
      !!orgName ||
      !manualOrder ||
      previousColumns.length !== calculatedColumns.length,
    [],
  );

  const rows = useMemo(
    () =>
      loading || !animalDetails?.animalDetails || !data?.animals.results
        ? []
        : data.animals.results.map(entityToRowData(columnHeaders)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.animals, loading, animalDetails?.animalDetails],
  );

  const entities = useMemo(() => data?.animals.results || [], [data?.animals]);

  return {
    data,
    previousData,
    error,
    loading,
    done: (data || error) && !loading,
    dataGrid: {
      getRowId: (row: TRow) => row[DEFAULT_ANIMAL_DETAIL.ID].value.toString(),
      filterModel: undefined,
      columns: columns as unknown as GridColDef<any>[],
      rows: rows as any,
    },
    defaultColumnFieldOrder: defaultAnimalDetailIdColumnOrder,
    entities,
    resetColumnOrder: () => setManualOrder(false),
    startSearch,
  };
}
